import { UsageRightActivationState } from '@markant-platform/usage-right/model';
import {
  Address,
  BookingRequestType,
  Industry,
  OrganizationDetailData,
  OrganizationType,
  QualificationLevel,
  SalutationType,
} from 'data-model';

export interface Organization {
  id?: string;
  name: string;
  gln: string;
  address?: Address;
  organizationName?: string;
  billingEmail?: string;
  customerNumber?: number;
  organizationType?: OrganizationType;
  masterKin?: string;
  ezrCustomer?: boolean;
  qualificationLevel?: QualificationLevel | null;
  status?: string;
  createdAt?: string;
}

export interface SelectableSuborganization {
  id: string;
  name: string;
  selected: boolean;
}

export interface OrganizationDto {
  id: string;
  name: string;
}

export interface SubOrganizationData {
  id: string;
  organizationId: string;
  parentId: string;
  name: string;
  childIds: string[];
  glns: glnData[];
  status?: SuborganizationStatus;
  memberCount?: number;
  version?: number;
  path: SubOrganizationPathData[];
}

export interface SubOrgaExtraOptions {
  searchQuery?: string;
  sortDirection?: string;
  subOrganizationId?: string;
  flat?: boolean;
}

export interface SubOrganizationPathData {
  id: string;
  name: string;
}

export interface CreateSubOrganizationDTO {
  parentId: string;
  name: string;
  glns?: glnData[];
}

export interface CreateSubOrganizationResponseDTO {
  id: string;
  organizationId: string;
  parentId: string;
  name: string;
  glns: glnData[];
  status: SuborganizationStatus;
  version: number;
}

export interface EditSubOrganizationDTO {
  name?: string;
  glns?: glnData[];
  version: number;
}

export interface EditSubOrganizationResponseDTO {
  id: string;
  organizationId: string;
  parentId: string;
  name: string;
  glns: glnData[];
  validatedGlns: glnData[];
  childIds: string[];
  status: SuborganizationStatus;
  version: number;
}

export interface DeactivatedSubOrganization {
  suborganizationId: string;
  version: number;
}

export interface DeactivateSuborganizationsResponseDTO {
  deactivatedSuborganizationIds: string[];
  failures: Map<string, string>;
  withFailure: boolean;
}

export type SuborganizationStatus = 'ACTIVE' | 'DEACTIVATED';

export enum CustomerGroup {
  EXISTING = 'EXISTING',
  NEW = 'NEW',
}

export interface glnData {
  value: string;
}

export interface OrganizationFurtherQualificationRequestDto {
  id: string;
  gln?: string | null;
  address: Address;
  phone: string;
  salesTaxId: string | null;
  taxNumber: string | null;
  industry: Industry;
}

export interface FurtherQualificationUserRequestDto {
  userId?: string;
  salutation: SalutationType;
  firstname: string;
  lastname: string;
  email: string;
  language: string;
  position?: string;
  phone?: string;
  department?: string;
  termsOfUseVersion?: string;
  termsOfUseTimestamp?: Date;
}

export interface FurtherQualificationLevel3RequestDto {
  organization: OrganizationFurtherQualificationRequestDto;
  requester: FurtherQualificationUserRequestDto;
  admin: FurtherQualificationUserRequestDto;
}

export interface FurtherQualificationStatus {
  adminVerified: boolean;
  approvedByBackoffice: boolean;
}

export type CartridgeOverviewInfo = {
  id: string;
  name: string | null;
  description: string;
  usageRightActivationState?: UsageRightActivationState;
  serviceManager: string;
  bookingDate?: string;
  workflowFinishedAt?: string;
  bookingStartedAt?: string;
  validityPeriodEnd?: string;
  validityEndText?: string;
  showNotUiBookableChip?: string;
  requestType?: BookingRequestType;
};

export enum ApprovalStrategy {
  FULL = 'FULL',
  BOOKING_ONLY = 'BOOKING_ONLY',
  REGISTRATION_ONLY = 'REGISTRATION_ONLY',
}

export type OrganizationBookingApprovalDetails = Pick<
  OrganizationDetailData,
  | 'id'
  | 'name'
  | 'gln'
  | 'address'
  | 'phone'
  | 'salesTaxId'
  | 'taxNumber'
  | 'organizationType'
  | 'createdAt'
  | 'status'
  | 'ezrCustomer'
  | 'qualificationLevel'
  | 'billingEmail'
  | 'billingLanguageIsoCode'
  | 'version'
  | 'masterKin'
  | 'crmId'
  | 'industry'
  | 'markantId'
  | 'leadContext'
> & {
  approvalStrategy: ApprovalStrategy;
};

export type OrganizationBookingApprovalDetailsUpdate = Pick<
  OrganizationBookingApprovalDetails,
  'gln' | 'salesTaxId' | 'taxNumber' | 'phone' | 'version'
> & {
  changeReason: string;
};

export const TAX_SELECTED_OPTIONS = [{ value: 'salesTaxId' }, { value: 'taxNumber' }];

export enum TaxOptions {
  TAX_NUMBER = 'taxNumber',
  SALES_TAX_ID = 'salesTaxId',
}

export const OrganizationStateMap: Record<string, { color: string; icon: string }> = {
  active: { color: 'success', icon: 'm_checkmark-small' },
  deactivated: { color: 'error', icon: 'm_close-small' },
};
